import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { titleCase } from "../lib/utils";
import {
  ArrowLeft,
  GitBranch,
  Clock,
  CheckCircle2,
  XCircle,
  PlayCircle,
  Pencil,
  Trash2,
  Plus,
  Rocket,
  Calendar,
  ChevronRight,
  Target,
  LayoutGrid,
  Package,
  RotateCcw,
  History,
} from "lucide-react";
import {
  releaseCyclesApi,
  enhancedDeploymentsApi,
  servicesApi,
  environmentsApi,
  groupsApi,
  ReleaseCycle,
  ReleaseCycleStatus,
  EnhancedDeployment,
  DeploymentStatus,
  AuditLogEntry,
  Environment,
} from "../api/client";
import { formatRelativeTime } from "../lib/utils";
import ReleaseCycleDeploymentMatrix from "../components/ReleaseCycleDeploymentMatrix";
import RollupGenerationModal from "../components/RollupGenerationModal";
import ConfirmationModal from "../components/ConfirmationModal";

const cycleStatusConfig: Record<
  ReleaseCycleStatus,
  { label: string; icon: React.ReactNode; className: string }
> = {
  planning: {
    label: "Planning",
    icon: <Clock className="w-4 h-4" />,
    className: "bg-gray-100 text-gray-700",
  },
  in_progress: {
    label: "In Progress",
    icon: <PlayCircle className="w-4 h-4" />,
    className: "bg-blue-100 text-blue-700",
  },
  completed: {
    label: "Completed",
    icon: <CheckCircle2 className="w-4 h-4" />,
    className: "bg-green-100 text-green-700",
  },
  cancelled: {
    label: "Cancelled",
    icon: <XCircle className="w-4 h-4" />,
    className: "bg-red-100 text-red-700",
  },
};

const deploymentStatusConfig: Record<
  DeploymentStatus,
  { label: string; className: string }
> = {
  draft: { label: "Draft", className: "bg-gray-100 text-gray-700" },
  pending_approval: {
    label: "Pending Approval",
    className: "bg-yellow-100 text-yellow-700",
  },
  approved: { label: "Approved", className: "bg-blue-100 text-blue-700" },
  rejected: { label: "Rejected", className: "bg-red-100 text-red-700" },
  deploying: { label: "Deploying", className: "bg-purple-100 text-purple-700" },
  completed: { label: "Completed", className: "bg-green-100 text-green-700" },
  success: { label: "Success", className: "bg-green-100 text-green-700" },
  failed: { label: "Failed", className: "bg-red-100 text-red-700" },
  cancelled: { label: "Cancelled", className: "bg-gray-100 text-gray-500" },
};

type TabType = "overview" | "matrix" | "deployments" | "timeline" | "history";

export default function ReleaseCycleDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateDeploymentModal, setShowCreateDeploymentModal] =
    useState(false);
  const [showRollupModal, setShowRollupModal] = useState(false);
  const [editingDeployment, setEditingDeployment] =
    useState<EnhancedDeployment | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmLabel: string;
    variant: "danger" | "warning" | "info";
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    confirmLabel: "Confirm",
    variant: "warning",
    onConfirm: () => {},
  });
  const [, setDeploymentToDelete] = useState<EnhancedDeployment | null>(null);

  const { data: environmentsData } = useQuery({
    queryKey: ["environments"],
    queryFn: () => environmentsApi.list(),
  });

  const allEnvironments = environmentsData?.environments || [];

  const { data: releaseCycle, isLoading: cycleLoading } = useQuery({
    queryKey: ["release-cycle", id],
    queryFn: () => releaseCyclesApi.get(id!),
    enabled: !!id,
  });

  // Fetch SDLC path for the group to filter environments in the matrix
  const { data: sdlcPathData } = useQuery({
    queryKey: ["sdlc-path", releaseCycle?.group_id],
    queryFn: () => groupsApi.getSDLCPath(releaseCycle!.group_id!),
    enabled: !!releaseCycle?.group_id,
  });

  // Use SDLC path environments if available, otherwise fall back to all environments
  const environments: Environment[] = sdlcPathData?.in_path || allEnvironments;

  // Helper to check if a deployment's environment is production
  const isProductionDeployment = (environmentId: string) => {
    const env = environments.find((e: Environment) => e.id === environmentId);
    return env?.is_production ?? false;
  };

  const { data: deploymentsData } = useQuery({
    queryKey: ["release-cycle-deployments", id],
    queryFn: () => releaseCyclesApi.getDeployments(id!),
    enabled: !!id,
  });

  const deployments = deploymentsData?.deployments || [];

  const { data: historyData } = useQuery({
    queryKey: ["release-cycle-history", id],
    queryFn: () => releaseCyclesApi.getHistory(id!),
    enabled: !!id,
  });

  const history = historyData?.history || [];

  const updateStatusMutation = useMutation({
    mutationKey: ["updateReleaseCycleStatus"],
    mutationFn: (status: ReleaseCycleStatus) =>
      releaseCyclesApi.updateStatus(id!, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release-cycle", id] });
      queryClient.invalidateQueries({ queryKey: ["release-cycles"] });
    },
  });

  const deleteMutation = useMutation({
    mutationKey: ["deleteReleaseCycle"],
    mutationFn: () => releaseCyclesApi.delete(id!),
    onSuccess: async () => {
      // Invalidate the release cycles list before navigating
      await queryClient.invalidateQueries({ queryKey: ["release-cycles"] });
      navigate("/release-cycles");
    },
  });

  const deleteDeploymentMutation = useMutation({
    mutationKey: ["deleteDeployment"],
    mutationFn: (deploymentId: string) =>
      enhancedDeploymentsApi.delete(deploymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["release-cycle-deployments", id],
      });
    },
  });

  const closeConfirmModal = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
    setDeploymentToDelete(null);
  };

  const showConfirm = (
    title: string,
    message: string,
    onConfirm: () => void,
    options?: {
      confirmLabel?: string;
      variant?: "danger" | "warning" | "info";
    },
  ) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      confirmLabel: options?.confirmLabel || "Confirm",
      variant: options?.variant || "warning",
      onConfirm,
    });
  };

  const handleDeleteDeployment = (deployment: EnhancedDeployment) => {
    if (deployment.enhanced_status !== "draft") {
      showConfirm(
        "Cannot Delete Deployment",
        "Only draft deployments can be deleted. This deployment has already progressed past the draft stage.",
        () => closeConfirmModal(),
        { confirmLabel: "OK", variant: "info" },
      );
      return;
    }
    setDeploymentToDelete(deployment);
    showConfirm(
      "Delete Deployment",
      `Are you sure you want to delete the deployment "${deployment.title || `${titleCase(deployment.service_name || "")} → ${deployment.environment_name}`}"?`,
      () => {
        deleteDeploymentMutation.mutate(deployment.id);
        closeConfirmModal();
      },
      { confirmLabel: "Delete", variant: "danger" },
    );
  };

  const handleEditDeployment = (deployment: EnhancedDeployment) => {
    if (deployment.enhanced_status !== "draft") {
      showConfirm(
        "Cannot Edit Deployment",
        "Only draft deployments can be edited. This deployment has already progressed past the draft stage.",
        () => closeConfirmModal(),
        { confirmLabel: "OK", variant: "info" },
      );
      return;
    }
    setEditingDeployment(deployment);
  };

  if (cycleLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (!releaseCycle) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900">
            Release Cycle not found
          </h2>
          <button
            onClick={() => navigate("/release-cycles")}
            className="btn btn-primary mt-4"
          >
            Back to Release Cycles
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = cycleStatusConfig[releaseCycle.status];
  const isPlanning = releaseCycle.status === "planning";
  const isActive =
    releaseCycle.status === "planning" || releaseCycle.status === "in_progress";

  const formatTargetDate = (dateStr?: string) => {
    if (!dateStr) return null;
    // Parse as local date to avoid timezone shift
    // Date-only strings like "2026-01-18" are interpreted as UTC by default
    const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  // Group deployments by service and environment
  const deploymentsByService = deployments.reduce(
    (acc, dep) => {
      const serviceKey = dep.service_name || "Unknown Service";
      if (!acc[serviceKey]) {
        acc[serviceKey] = [];
      }
      acc[serviceKey].push(dep);
      return acc;
    },
    {} as Record<string, EnhancedDeployment[]>,
  );

  // Stats - check both enhanced_status and status fields
  const getStatus = (d: EnhancedDeployment) =>
    d.enhanced_status || d.status || "draft";
  const completedDeployments = deployments.filter(
    (d) => getStatus(d) === "completed",
  ).length;
  const pendingDeployments = deployments.filter(
    (d) =>
      getStatus(d) === "draft" ||
      getStatus(d) === "pending_approval" ||
      getStatus(d) === "approved",
  ).length;
  const failedDeployments = deployments.filter(
    (d) => getStatus(d) === "failed",
  ).length;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/release-cycles")}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Release Cycles
        </button>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <GitBranch className="w-8 h-8 text-brand-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {releaseCycle.title}
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-brand-100 text-brand-800">
                    v{releaseCycle.customer_version}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusInfo.className}`}
                  >
                    {statusInfo.icon}
                    {statusInfo.label}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
              {releaseCycle.group_name && (
                <span>
                  <strong>Group:</strong> {releaseCycle.group_name}
                </span>
              )}
              {releaseCycle.target_prod_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Target: {formatTargetDate(releaseCycle.target_prod_date)}
                </span>
              )}
              <span>Created {formatRelativeTime(releaseCycle.created_at)}</span>
            </div>
            {releaseCycle.description && (
              <p className="text-gray-600 mt-2">{releaseCycle.description}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {isPlanning && (
              <button
                onClick={() => updateStatusMutation.mutate("in_progress")}
                disabled={updateStatusMutation.isPending}
                className="btn btn-primary"
              >
                <PlayCircle className="w-4 h-4 mr-2" />
                Start Cycle
              </button>
            )}
            {releaseCycle.status === "in_progress" && (
              <button
                onClick={() => {
                  const completedCount = deployments.filter(
                    (d) => getStatus(d) === "completed",
                  ).length;
                  const totalCount = deployments.length;
                  const message =
                    totalCount === 0
                      ? "This release cycle has no deployments. Are you sure you want to mark it as completed?"
                      : completedCount < totalCount
                        ? `Only ${completedCount} of ${totalCount} deployments are completed. Are you sure you want to complete this release cycle?`
                        : "Are you sure you want to complete this release cycle?";
                  showConfirm(
                    "Complete Release Cycle",
                    message,
                    () => {
                      updateStatusMutation.mutate("completed");
                      closeConfirmModal();
                    },
                    {
                      confirmLabel: "Complete",
                      variant:
                        totalCount === 0 || completedCount < totalCount
                          ? "warning"
                          : "info",
                    },
                  );
                }}
                disabled={updateStatusMutation.isPending}
                className="btn btn-primary"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Complete Cycle
              </button>
            )}
            {(releaseCycle.status === "completed" ||
              releaseCycle.status === "cancelled") && (
              <button
                onClick={() => {
                  showConfirm(
                    "Reopen Release Cycle",
                    "Are you sure you want to reopen this release cycle? It will be set back to 'In Progress' status.",
                    () => {
                      updateStatusMutation.mutate("in_progress");
                      closeConfirmModal();
                    },
                    { confirmLabel: "Reopen", variant: "info" },
                  );
                }}
                disabled={updateStatusMutation.isPending}
                className="btn btn-secondary"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reopen Cycle
              </button>
            )}
            {isActive && (
              <>
                <button
                  onClick={() => setShowEditModal(true)}
                  className="btn btn-secondary"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    showConfirm(
                      "Cancel Release Cycle",
                      "Are you sure you want to cancel this release cycle? This action can be undone by reopening the cycle.",
                      () => {
                        updateStatusMutation.mutate("cancelled");
                        closeConfirmModal();
                      },
                      { confirmLabel: "Cancel Cycle", variant: "danger" },
                    );
                  }}
                  disabled={updateStatusMutation.isPending}
                  className="btn btn-secondary text-red-600 hover:text-red-700"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </>
            )}
            {completedDeployments === 0 && (
              <button
                onClick={() => {
                  const message =
                    deployments.length === 0
                      ? "Are you sure you want to delete this release cycle? This action cannot be undone."
                      : `Are you sure you want to delete this release cycle? This will also delete ${deployments.length} deployment(s). This action cannot be undone.`;
                  showConfirm(
                    "Delete Release Cycle",
                    message,
                    () => {
                      deleteMutation.mutate();
                      closeConfirmModal();
                    },
                    { confirmLabel: "Delete", variant: "danger" },
                  );
                }}
                disabled={deleteMutation.isPending}
                className="btn btn-secondary text-red-600 hover:text-red-700"
                title="Delete release cycle"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-8">
          {[
            { id: "overview", label: "Overview", icon: GitBranch },
            { id: "matrix", label: "Matrix", icon: LayoutGrid },
            {
              id: "deployments",
              label: "Deployments",
              icon: Rocket,
              count: deployments.length,
            },
            { id: "timeline", label: "Timeline", icon: Clock },
            {
              id: "history",
              label: "History",
              icon: History,
              count: history.length > 0 ? history.length : undefined,
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`flex items-center gap-2 py-3 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "border-brand-600 text-brand-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stats Cards */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-brand-100 rounded-lg">
                <Rocket className="w-5 h-5 text-brand-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {deployments.length}
                </div>
                <div className="text-sm text-gray-500">Total Deployments</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="bg-green-50 rounded p-2">
                <div className="font-semibold text-green-700">
                  {completedDeployments}
                </div>
                <div className="text-green-600">Completed</div>
              </div>
              <div className="bg-yellow-50 rounded p-2">
                <div className="font-semibold text-yellow-700">
                  {pendingDeployments}
                </div>
                <div className="text-yellow-600">Pending</div>
              </div>
              <div className="bg-red-50 rounded p-2">
                <div className="font-semibold text-red-700">
                  {failedDeployments}
                </div>
                <div className="text-red-600">Failed</div>
              </div>
            </div>
          </div>

          {/* Services Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Target className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {Object.keys(deploymentsByService).length}
                </div>
                <div className="text-sm text-gray-500">Services</div>
              </div>
            </div>
            <div className="space-y-2">
              {Object.keys(deploymentsByService)
                .slice(0, 4)
                .map((service) => (
                  <div key={service} className="flex justify-between text-sm">
                    <span className="text-gray-600">{service}</span>
                    <span className="text-gray-900 font-medium">
                      {deploymentsByService[service].length} deploys
                    </span>
                  </div>
                ))}
              {Object.keys(deploymentsByService).length > 4 && (
                <div className="text-sm text-gray-500">
                  +{Object.keys(deploymentsByService).length - 4} more...
                </div>
              )}
            </div>
          </div>

          {/* Target Date Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900">
                  {releaseCycle.target_prod_date
                    ? formatTargetDate(releaseCycle.target_prod_date)
                    : "Not Set"}
                </div>
                <div className="text-sm text-gray-500">
                  Target Production Date
                </div>
              </div>
            </div>
            {releaseCycle.target_prod_date && (
              <div className="text-sm text-gray-600">
                {(() => {
                  const target = new Date(releaseCycle.target_prod_date);
                  const now = new Date();
                  const diff = Math.ceil(
                    (target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
                  );
                  if (diff < 0) {
                    return (
                      <span className="text-red-600">
                        {Math.abs(diff)} days overdue
                      </span>
                    );
                  } else if (diff === 0) {
                    return <span className="text-orange-600">Due today</span>;
                  } else if (diff <= 7) {
                    return (
                      <span className="text-orange-600">
                        {diff} days remaining
                      </span>
                    );
                  } else {
                    return (
                      <span className="text-gray-600">
                        {diff} days remaining
                      </span>
                    );
                  }
                })()}
              </div>
            )}
          </div>

          {/* Deployments */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 lg:col-span-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Deployments
              </h3>
              {isActive && (
                <button
                  onClick={() => setShowCreateDeploymentModal(true)}
                  className="btn btn-primary btn-sm"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Deployment
                </button>
              )}
            </div>
            {deployments.length === 0 ? (
              <div className="text-center py-8">
                <Rocket className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No deployments yet</p>
                {isActive && (
                  <button
                    onClick={() => setShowCreateDeploymentModal(true)}
                    className="btn btn-primary mt-4"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Deployment
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {deployments.slice(0, 5).map((deployment) => {
                  const status =
                    deploymentStatusConfig[
                      deployment.enhanced_status || deployment.status || "draft"
                    ];
                  return (
                    <div
                      key={deployment.id}
                      onClick={() => navigate(`/deployments/${deployment.id}`)}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <Rocket className="w-5 h-5 text-gray-400" />
                        <div>
                          <div className="font-medium text-gray-900">
                            {deployment.title ||
                              `${titleCase(deployment.service_name || "")} - ${deployment.environment_name}`}
                          </div>
                          <div className="text-sm text-gray-500">
                            {titleCase(deployment.service_name || "")} →{" "}
                            {deployment.environment_name}
                            {deployment.sequence_type &&
                              !isProductionDeployment(
                                deployment.environment_id,
                              ) && (
                                <span className="ml-2 text-xs px-1.5 py-0.5 bg-gray-200 rounded">
                                  {deployment.sequence_type.toUpperCase()}
                                  {deployment.sequence_number &&
                                    ` #${deployment.sequence_number}`}
                                </span>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-medium ${status.className}`}
                        >
                          {status.label}
                        </span>
                        {deployment.enhanced_status === "draft" && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditDeployment(deployment);
                              }}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit deployment"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteDeployment(deployment);
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Delete deployment"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  );
                })}
                {deployments.length > 5 && (
                  <button
                    onClick={() => setActiveTab("deployments")}
                    className="w-full text-center text-sm text-brand-600 hover:text-brand-700 py-2"
                  >
                    View all {deployments.length} deployments
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "matrix" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Deployment Matrix
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                View deployments by service and environment
              </p>
            </div>
            <div className="flex gap-2">
              {isActive && (
                <>
                  <button
                    onClick={() => setShowRollupModal(true)}
                    className="btn btn-secondary"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Generate Rollups
                  </button>
                  <button
                    onClick={() => setShowCreateDeploymentModal(true)}
                    className="btn btn-primary"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Deployment
                  </button>
                </>
              )}
            </div>
          </div>

          <ReleaseCycleDeploymentMatrix
            deployments={deployments}
            environments={environments}
            onCreateDeployment={
              isActive
                ? (_serviceId, _environmentId) => {
                    // TODO: Pre-fill the create deployment modal with service/env
                    setShowCreateDeploymentModal(true);
                  }
                : undefined
            }
          />
        </div>
      )}

      {activeTab === "deployments" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              All Deployments
            </h3>
            {isActive && (
              <button
                onClick={() => setShowCreateDeploymentModal(true)}
                className="btn btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Deployment
              </button>
            )}
          </div>

          {Object.keys(deploymentsByService).length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <Rocket className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No deployments yet
              </h3>
              <p className="text-gray-600 mb-4">
                Add deployments to track releases for this cycle
              </p>
              {isActive && (
                <button
                  onClick={() => setShowCreateDeploymentModal(true)}
                  className="btn btn-primary"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Deployment
                </button>
              )}
            </div>
          ) : (
            Object.entries(deploymentsByService).map(([service, deps]) => (
              <div
                key={service}
                className="bg-white rounded-lg border border-gray-200"
              >
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h4 className="font-semibold text-gray-900">{service}</h4>
                </div>
                <div className="divide-y divide-gray-200">
                  {deps.map((deployment) => {
                    const status =
                      deploymentStatusConfig[
                        deployment.enhanced_status ||
                          deployment.status ||
                          "draft"
                      ];
                    return (
                      <div
                        key={deployment.id}
                        onClick={() =>
                          navigate(`/deployments/${deployment.id}`)
                        }
                        className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                            <Rocket className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {deployment.title ||
                                `${deployment.environment_name} Deployment`}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center gap-2">
                              <span>{deployment.environment_name}</span>
                              <span>•</span>
                              <span className="font-mono">
                                {deployment.version}
                              </span>
                              {deployment.sequence_type &&
                                !isProductionDeployment(
                                  deployment.environment_id,
                                ) && (
                                  <>
                                    <span>•</span>
                                    <span className="uppercase text-xs">
                                      {deployment.sequence_type}
                                      {deployment.sequence_number &&
                                        ` #${deployment.sequence_number}`}
                                    </span>
                                  </>
                                )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${status.className}`}
                          >
                            {status.label}
                          </span>
                          <div className="text-sm text-gray-500">
                            {formatRelativeTime(deployment.created_at)}
                          </div>
                          {deployment.enhanced_status === "draft" && (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditDeployment(deployment);
                                }}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                title="Edit deployment"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteDeployment(deployment);
                                }}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Delete deployment"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === "timeline" && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">
            Release Cycle Timeline
          </h3>
          {deployments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No timeline events yet
            </div>
          ) : (
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
              <div className="space-y-6">
                {deployments
                  .sort(
                    (a, b) =>
                      new Date(b.created_at).getTime() -
                      new Date(a.created_at).getTime(),
                  )
                  .map((deployment) => {
                    const status =
                      deploymentStatusConfig[
                        deployment.enhanced_status ||
                          deployment.status ||
                          "draft"
                      ];
                    return (
                      <div key={deployment.id} className="relative pl-10">
                        <div
                          className={`absolute left-2 w-5 h-5 rounded-full border-2 border-white ${
                            deployment.enhanced_status === "completed"
                              ? "bg-green-500"
                              : deployment.enhanced_status === "failed"
                                ? "bg-red-500"
                                : deployment.enhanced_status === "deploying"
                                  ? "bg-purple-500"
                                  : "bg-gray-300"
                          }`}
                        />
                        <div
                          onClick={() =>
                            navigate(`/deployments/${deployment.id}`)
                          }
                          className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 cursor-pointer"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">
                              {deployment.title ||
                                `${titleCase(deployment.service_name || "")} → ${deployment.environment_name}`}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}
                            >
                              {status.label}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatRelativeTime(deployment.created_at)}
                            {deployment.created_by_name &&
                              ` by ${deployment.created_by_name}`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === "history" && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">
            Change History
          </h3>
          {history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p>No changes recorded yet</p>
              <p className="text-sm mt-1">
                Target date changes will be tracked here after the release cycle
                starts
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry) => (
                <HistoryEntry key={entry.id} entry={entry} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <EditReleaseCycleModal
          releaseCycle={releaseCycle}
          onClose={() => setShowEditModal(false)}
          onUpdated={() => {
            setShowEditModal(false);
            queryClient.invalidateQueries({ queryKey: ["release-cycle", id] });
            queryClient.invalidateQueries({
              queryKey: ["release-cycle-history", id],
            });
          }}
        />
      )}

      {/* Create Deployment Modal */}
      {showCreateDeploymentModal && (
        <CreateDeploymentModal
          releaseCycleId={id!}
          groupSlug={releaseCycle.group_slug}
          onClose={() => setShowCreateDeploymentModal(false)}
          onCreated={(deploymentId) => {
            setShowCreateDeploymentModal(false);
            queryClient.invalidateQueries({
              queryKey: ["release-cycle-deployments", id],
            });
            navigate(`/deployments/${deploymentId}`);
          }}
        />
      )}

      {/* Rollup Generation Modal */}
      {showRollupModal && (
        <RollupGenerationModal
          releaseCycleId={id!}
          onClose={() => setShowRollupModal(false)}
          onGenerated={(deploymentIds) => {
            setShowRollupModal(false);
            queryClient.invalidateQueries({
              queryKey: ["release-cycle-deployments", id],
            });
            // Navigate to the first generated deployment
            if (deploymentIds.length > 0) {
              navigate(`/deployments/${deploymentIds[0]}`);
            }
          }}
        />
      )}

      {/* Edit Deployment Modal */}
      {editingDeployment && (
        <EditDeploymentModal
          deployment={editingDeployment}
          onClose={() => setEditingDeployment(null)}
          onUpdated={() => {
            setEditingDeployment(null);
            queryClient.invalidateQueries({
              queryKey: ["release-cycle-deployments", id],
            });
          }}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        variant={confirmModal.variant}
        isLoading={
          updateStatusMutation.isPending ||
          deleteMutation.isPending ||
          deleteDeploymentMutation.isPending
        }
      />
    </div>
  );
}

interface EditReleaseCycleModalProps {
  releaseCycle: ReleaseCycle;
  onClose: () => void;
  onUpdated: () => void;
}

function EditReleaseCycleModal({
  releaseCycle,
  onClose,
  onUpdated,
}: EditReleaseCycleModalProps) {
  const [formData, setFormData] = useState({
    customer_version: releaseCycle.customer_version,
    title: releaseCycle.title,
    description: releaseCycle.description || "",
    target_prod_date: releaseCycle.target_prod_date?.split("T")[0] || "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await releaseCyclesApi.update(releaseCycle.id, {
        customer_version: formData.customer_version,
        title: formData.title,
        description: formData.description || undefined,
        target_prod_date: formData.target_prod_date || undefined,
      });
      onUpdated();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update release cycle",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Edit Release Cycle
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Version
                {releaseCycle.status === "planning" && (
                  <span className="text-red-500"> *</span>
                )}
              </label>
              <input
                type="text"
                value={formData.customer_version}
                onChange={(e) => {
                  let value = e.target.value;
                  // Strip leading 'v' or 'V' to prevent "vv" display
                  if (value.toLowerCase().startsWith("v")) {
                    value = value.slice(1);
                  }
                  setFormData({ ...formData, customer_version: value });
                }}
                disabled={releaseCycle.status !== "planning"}
                className={
                  releaseCycle.status === "planning"
                    ? "input w-full"
                    : "input w-full bg-gray-100 text-gray-500 cursor-not-allowed"
                }
                required={releaseCycle.status === "planning"}
              />
              {releaseCycle.status !== "planning" && (
                <p className="text-xs text-gray-500 mt-1">
                  Version cannot be changed after release cycle has started
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
                {releaseCycle.status === "planning" && (
                  <span className="text-red-500"> *</span>
                )}
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                disabled={releaseCycle.status !== "planning"}
                className={
                  releaseCycle.status === "planning"
                    ? "input w-full"
                    : "input w-full bg-gray-100 text-gray-500 cursor-not-allowed"
                }
                required={releaseCycle.status === "planning"}
              />
              {releaseCycle.status !== "planning" && (
                <p className="text-xs text-gray-500 mt-1">
                  Title cannot be changed after release cycle has started
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={3}
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Production Date
              </label>
              <input
                type="date"
                value={formData.target_prod_date}
                onChange={(e) =>
                  setFormData({ ...formData, target_prod_date: e.target.value })
                }
                className="input w-full"
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Helper function to format date for display
function formatDateForDisplay(dateStr: string | null): string {
  if (!dateStr) return "Not set";
  // Parse as local date to avoid timezone shift
  const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Helper function to get action label
function getActionLabel(action: string): string {
  switch (action) {
    case "release_cycle.target_date_changed":
      return "Target Date Changed";
    case "release_cycle.updated":
      return "Release Cycle Updated";
    default:
      return action.replace(/_/g, " ").replace(/\./g, " - ");
  }
}

function HistoryEntry({ entry }: { entry: AuditLogEntry }) {
  const changes = entry.changes || {};
  const targetDateChange = changes.target_prod_date;

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-blue-100 rounded">
            <Calendar className="w-4 h-4 text-blue-600" />
          </div>
          <span className="font-medium text-gray-900">
            {getActionLabel(entry.action)}
          </span>
        </div>
        <span className="text-sm text-gray-500">
          {formatRelativeTime(entry.created_at)}
        </span>
      </div>

      {targetDateChange && (
        <div className="mt-3 pl-8">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">From:</span>
            <span className="font-medium text-gray-700">
              {formatDateForDisplay(targetDateChange.old)}
            </span>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="text-gray-500">To:</span>
            <span className="font-medium text-gray-900">
              {formatDateForDisplay(targetDateChange.new)}
            </span>
          </div>
        </div>
      )}

      {entry.user_name && (
        <div className="mt-2 pl-8 text-sm text-gray-500">
          Changed by {entry.user_name}
        </div>
      )}
    </div>
  );
}

interface CreateDeploymentModalProps {
  releaseCycleId: string;
  groupSlug?: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}

function CreateDeploymentModal({
  releaseCycleId,
  groupSlug,
  onClose,
  onCreated,
}: CreateDeploymentModalProps) {
  // Services state: first service is primary, rest are secondary
  const [selectedServices, setSelectedServices] = useState<string[]>([""]);
  const [formData, setFormData] = useState({
    environment_id: "",
    version: "",
    title: "",
    description: "",
    sequence_type: "rc" as "rc" | "hotfix" | "rollup",
    sequence_number: 1,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: servicesData } = useQuery({
    queryKey: ["services", groupSlug],
    queryFn: () => servicesApi.list({ group: groupSlug }),
  });

  const { data: environmentsData } = useQuery({
    queryKey: ["environments"],
    queryFn: () => environmentsApi.list(),
  });

  const services = servicesData?.services || [];
  const environments = environmentsData?.environments || [];

  // Check if selected environment is production
  const selectedEnvironment = environments.find(
    (env) => env.id === formData.environment_id,
  );
  const isProductionEnv = selectedEnvironment?.is_production ?? false;

  // Get available services (exclude already selected ones)
  const getAvailableServices = (currentIndex: number) => {
    const selectedIds = selectedServices.filter((_, i) => i !== currentIndex);
    return services.filter((s) => !selectedIds.includes(s.id));
  };

  const handleAddService = () => {
    setSelectedServices([...selectedServices, ""]);
  };

  const handleRemoveService = (index: number) => {
    if (selectedServices.length > 1) {
      setSelectedServices(selectedServices.filter((_, i) => i !== index));
    }
  };

  const handleServiceChange = (index: number, serviceId: string) => {
    const newServices = [...selectedServices];
    newServices[index] = serviceId;
    setSelectedServices(newServices);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const validServices = selectedServices.filter((s) => s !== "");

      if (validServices.length === 0) {
        setError("Please select at least one service");
        setIsSubmitting(false);
        return;
      }

      if (validServices.length === 1) {
        // Single service deployment - use existing endpoint
        const result = await enhancedDeploymentsApi.createDraft({
          service_id: validServices[0],
          environment_id: formData.environment_id,
          version: formData.version,
          title: formData.title || undefined,
          description: formData.description || undefined,
          release_cycle_id: releaseCycleId,
          sequence_type: formData.sequence_type,
          sequence_number: formData.sequence_number,
        });
        onCreated(result.id);
      } else {
        // Multi-service deployment
        const result = await enhancedDeploymentsApi.createMultiServiceDraft({
          primary_service_id: validServices[0],
          secondary_service_ids: validServices.slice(1),
          environment_id: formData.environment_id,
          version: formData.version,
          title: formData.title || undefined,
          description: formData.description || undefined,
          release_cycle_id: releaseCycleId,
          sequence_type: formData.sequence_type,
          sequence_number: formData.sequence_number,
        });
        onCreated(result.primary.id);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create deployment",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Create Deployment
          </h2>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                {error}
              </div>
            )}

            {/* Environment Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Environment <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.environment_id}
                onChange={(e) =>
                  setFormData({ ...formData, environment_id: e.target.value })
                }
                className="input w-full"
                required
              >
                <option value="">Select environment</option>
                {environments.map((env) => (
                  <option key={env.id} value={env.id}>
                    {env.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Services Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Services <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {selectedServices.map((serviceId, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <select
                        value={serviceId}
                        onChange={(e) =>
                          handleServiceChange(index, e.target.value)
                        }
                        className="input w-full pr-8"
                        required={index === 0}
                      >
                        <option value="">Select service</option>
                        {getAvailableServices(index).map((service) => (
                          <option key={service.id} value={service.id}>
                            {titleCase(service.name)}
                          </option>
                        ))}
                      </select>
                      {index === 0 && serviceId && (
                        <span className="absolute right-8 top-1/2 -translate-y-1/2 text-xs text-amber-600 font-medium">
                          Primary
                        </span>
                      )}
                    </div>
                    {selectedServices.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveService(index)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove service"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={handleAddService}
                disabled={selectedServices.length >= services.length}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Service
              </button>
              {selectedServices.filter((s) => s !== "").length > 1 && (
                <p className="mt-1 text-xs text-gray-500">
                  Multi-service deployment: all services will share release
                  notes and approval
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Version <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) =>
                  setFormData({ ...formData, version: e.target.value })
                }
                placeholder="e.g., 3.14.0"
                className="input w-full"
                required
              />
            </div>

            {/* Only show sequence fields for non-production environments */}
            {!isProductionEnv && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sequence Type
                  </label>
                  <select
                    value={formData.sequence_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sequence_type: e.target.value as
                          | "rc"
                          | "hotfix"
                          | "rollup",
                      })
                    }
                    className="input w-full"
                  >
                    <option value="rc">Release Candidate (RC)</option>
                    <option value="hotfix">Hotfix</option>
                    <option value="rollup">Rollup</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sequence Number
                  </label>
                  <input
                    type="number"
                    value={formData.sequence_number}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sequence_number: parseInt(e.target.value) || 1,
                      })
                    }
                    min={1}
                    className="input w-full"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Optional custom title"
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Optional description..."
                rows={3}
                className="input w-full"
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating..." : "Create Deployment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface EditDeploymentModalProps {
  deployment: EnhancedDeployment;
  onClose: () => void;
  onUpdated: () => void;
}

function EditDeploymentModal({
  deployment,
  onClose,
  onUpdated,
}: EditDeploymentModalProps) {
  const [formData, setFormData] = useState({
    version: deployment.version,
    title: deployment.title || "",
    description: deployment.description || "",
    sequence_type: (deployment.sequence_type || "rc") as
      | "rc"
      | "hotfix"
      | "rollup",
    sequence_number: deployment.sequence_number || 1,
    notes: deployment.notes || "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await enhancedDeploymentsApi.update(deployment.id, {
        version: formData.version,
        title: formData.title || undefined,
        description: formData.description || undefined,
        sequence_type: formData.sequence_type,
        sequence_number: formData.sequence_number,
        notes: formData.notes || undefined,
      });
      onUpdated();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update deployment",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Edit Deployment
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {titleCase(deployment.service_name || "")} →{" "}
            {deployment.environment_name}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Version <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) =>
                  setFormData({ ...formData, version: e.target.value })
                }
                placeholder="e.g., 3.14.0"
                className="input w-full"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sequence Type
                </label>
                <select
                  value={formData.sequence_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sequence_type: e.target.value as
                        | "rc"
                        | "hotfix"
                        | "rollup",
                    })
                  }
                  className="input w-full"
                >
                  <option value="rc">Release Candidate (RC)</option>
                  <option value="hotfix">Hotfix</option>
                  <option value="rollup">Rollup</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sequence Number
                </label>
                <input
                  type="number"
                  value={formData.sequence_number}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sequence_number: parseInt(e.target.value) || 1,
                    })
                  }
                  min={1}
                  className="input w-full"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Optional custom title"
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Optional description..."
                rows={3}
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Optional notes..."
                rows={2}
                className="input w-full"
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
